/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface LogNewMediaContainerFormProps {
  formData: {
    containerTypeId: string;
    capacityId: string;
    manufacturerLotNumber: string;
    arrivalDate: string;
    totalContainers: string;
    noOfLots: string;
  };
  containerTypes: any[];
  capacityList: any[];
  errors: any;
  onChange: (field: string, value: any) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
}

const LogNewMediaContainerForm: React.FC<LogNewMediaContainerFormProps> = ({
  formData,
  containerTypes,
  capacityList,
  errors,
  onChange,
  onSave,
  onCancel,
  isSaving,
}) => {
  const { t } = useTranslation();

  const containerPerLot = useMemo(() => {
  const total = Number(formData.totalContainers);
  const lots = Number(formData.noOfLots);
  return total > 0 && lots > 0
      ? Math.floor(total / lots)
      : 0;
  }, [formData.totalContainers, formData.noOfLots]);

  return (
    <div className="media-prep-scope p-4 max-w-full mx-auto animate-in fade-in slide-in-from-bottom duration-500 pb-24">
        
        {/* STEP 1: Dropdown-driven Container Selection */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-3">
          <div className="px-6 py-3 bg-white border-b border-slate-100 flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-[#2D3E50] text-white flex items-center justify-center text-xs font-black shadow-sm relative -top-[3px]">01</div>
            <h3 className="text-xs font-black text-[#2D3E50] uppercase tracking-widest">{t('containerInventory.addContainer.CatalogSelection')}</h3>
          </div>

          <div className="px-8 pt-2 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-[90%]">
              {/* Category Dropdown */}
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block text-left">{t('containerInventory.addContainer.ContainerCategory')} <span className="text-red-500">*</span></label>
                <div className="relative group">
                  <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-orange-500 transition-colors">{t('containerInventory.icons.category')}</span>
                  <select 
                    value={formData.containerTypeId}
                    onChange={(e) => {
                      onChange("containerTypeId", e.target.value);
                      onChange("capacityId", "");
                    }}
                    className="w-full rounded-lg border border-slate-300 focus:outline-none focus:ring-1 focus:ring-[#FF7043] focus:border-[#FF7043] h-10 pl-14 pr-6 text-sm font-medium text-slate-700 appearance-none bg-white"
                  >
                    <option value="">{t('containerInventory.addContainer.containerTypePlaceholder')}</option>
                    {containerTypes.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                  {errors.containerTypeId && (<p className="block w-full text-left text-red-500 text-xs mt-1">{errors.containerTypeId}</p>)}
                  <span className="material-symbols-outlined absolute right-5 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none">{t('containerInventory.icons.expand_more')}</span>
                </div>
              </div>

              {/* Dimension Dropdown (Dependent) */}
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block text-left">{t('containerInventory.addContainer.Dimension')} <span className="text-red-500">*</span></label>
                <div className="relative group">
                  <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-orange-500 transition-colors">{t('containerInventory.icons.straighten')}</span>
                  <select 
                    value={formData.capacityId}
                    onChange={(e) => onChange("capacityId", e.target.value)}
                    disabled={!formData.containerTypeId}
                    className={`w-full rounded-lg h-10 pl-14 pr-6 text-sm font-medium appearance-none ${
                      !formData.containerTypeId
                      ? 'border border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                      : 'border border-slate-300 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#FF7043] focus:border-[#FF7043] cursor-pointer'
                    }`}
                  >
                    <option value="">
                      {formData.containerTypeId
                        ? t('containerInventory.addContainer.Selectdimension')
                        : t('containerInventory.addContainer.Choosecategory')}
                    </option>
                    {capacityList.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.size} {item.uom}
                      </option>
                    ))}
                  </select>
                  {errors.capacityId && (<p className="block w-full text-left text-red-500 text-xs mt-1">{errors.capacityId}</p>)}
                  <span className="material-symbols-outlined absolute right-5 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none">{t('containerInventory.icons.expand_more')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* STEP 2: Inventory Logistics */}
        <div className="col-span-12 lg:col-span-4 space-y-8">
           <div className="bg-white rounded-[1rem] border border-slate-200 shadow-sm overflow-hidden mb-3">
              <div className="px-6 py-3 bg-white border-b border-slate-100 flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-[#2D3E50] text-white flex items-center justify-center text-xs font-black shadow-sm relative -top-[3px]">02</div>
                <h3 className="text-xs font-black text-[#2D3E50] uppercase tracking-widest">{t('containerInventory.addContainer.InventoryLogistics')}</h3>
              </div>
              <div className="px-8 pt-2 pb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-[90%]">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block text-left">{t('containerInventory.addContainer.LotNumber')} <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={formData.manufacturerLotNumber}
                    onChange={(e) =>
                      onChange("manufacturerLotNumber", e.target.value)
                    }
                    placeholder={t('containerInventory.addContainer.lotPlaceholder')}
                    className="w-full h-10 rounded-lg border border-slate-300 focus:outline-none focus:ring-1 focus:ring-[#FF7043] focus:border-[#FF7043] px-4 text-sm font-medium text-slate-700 bg-white focus:ring-8 focus:ring-orange-500/5 focus:bg-white focus:border-[#FF7043] outline-none transition-all"
                  />
                  {errors.manufacturerLotNumber && (<p className="block w-full text-left text-red-500 text-xs mt-1">{errors.manufacturerLotNumber}</p>)}
                </div>
                
                <div className="space-y-1">
                   <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block text-left">{t('containerInventory.addContainer.ArrivalDate')} <span className="text-red-500">*</span></label>
                   <input
                      type="date"
                      value={formData.arrivalDate}
                      onChange={(e) =>onChange("arrivalDate", e.target.value)}
                      max={new Date().toISOString().split("T")[0]}
                      className="w-full h-10 rounded-lg border border-slate-300 focus:outline-none focus:ring-1 focus:ring-[#FF7043] focus:border-[#FF7043] px-4 text-sm font-medium text-slate-700 bg-white focus:bg-white outline-none"
                    />
                    {errors.arrivalDate && (<p className="block w-full text-left text-red-500 text-xs mt-1">{errors.arrivalDate}</p>)}
                </div>
              </div>
                
                {/* Visual Context card for Step 2 */}
                <div className="w-[90%] h-9 mt-4 bg-slate-50 border border-slate-100 rounded-lg px-3 flex items-center gap-1 overflow-hidden">
                  <span className="material-symbols-outlined text-slate-300 text-sm relative top-[-2px]">{t('containerInventory.icons.info')}</span>
                  <p className="text-[11px] font-medium text-slate-400 leading-none relative top-[5px]">{t('containerInventory.addContainer.infoText')}</p>
                </div>
              </div>
           </div>
        </div>

        {/* STEP 3: Quantity Accounting */}
        <div className="col-span-12 lg:col-span-8">
           <div className="bg-white rounded-[1rem] border border-slate-200 shadow-sm overflow-hidden h-full flex flex-col">
              <div className="px-10 py-3 border-b border-slate-100 bg-slate-50/30 flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-[#2D3E50] text-white flex items-center justify-center text-xs font-black shadow-sm relative -top-[3px]">03</div>
                <h3 className="text-xs font-black text-[#2D3E50] uppercase tracking-widest">{t('containerInventory.addContainer.QuantityAccounting')}</h3>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                 <div className="space-y-4">
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block text-left">{t('containerInventory.addContainer.Unity')} <span className="text-red-500">*</span></label>
                       <div className="relative w-[240px]">
                          <input 
                            type="number" 
                            value={formData.totalContainers} 
                            onChange={(e) => onChange("totalContainers", e.target.value)}
                            className="w-[240px] h-10 pl-12 pr-4 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-700 outline-none focus:ring-1 focus:ring-[#FF7043] focus:border-[#FF7043]"
                            placeholder="0"
                          />
                          {errors.totalContainers && (<p className="block w-full text-left text-red-500 text-xs mt-1">{errors.totalContainers}</p>)}
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-300 text-lg">{t('containerInventory.icons.inventory_2')}</span>
                       </div>
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block text-left">{t('containerInventory.addContainer.noOfLots')} <span className="text-red-500">*</span></label>
                       <div className="relative w-[240px]">
                          <input 
                            type="number" 
                            value={formData.noOfLots} 
                            onChange={(e) => onChange("noOfLots", e.target.value)}
                            className="w-[240px] h-10 pl-12 pr-4 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-700 outline-none focus:ring-1 focus:ring-[#FF7043] focus:border-[#FF7043]"
                            placeholder="0"
                          />
                          {errors.noOfLots && (<p className="block w-full text-left text-red-500 text-xs mt-1">{errors.noOfLots}</p>)}
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-300 text-lg">{t('containerInventory.icons.layers')}</span>
                       </div>
                    </div>
                 </div>

                 <div className="flex flex-col -mt-12">
                    <div className="flex-1 bg-slate-50 border border-slate-100 rounded-[3rem] p-4 h-[200px] w-[300px] flex flex-col items-center justify-center text-center gap-4 relative overflow-hidden group hover:shadow-2xl hover:shadow-slate-200/50 transition-all duration-500">
                      <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity">
                        <span className="material-symbols-outlined text-[120px]">{t('containerInventory.icons.equalizer')}</span>
                      </div>
                      <p className="text-[12px] font-black text-slate-400 uppercase tracking-[0.3em] relative z-10">{t('containerInventory.addContainer.AverageLoad')}</p>
                      <div className="flex items-baseline gap-3 relative z-10">
                        <span className={`text-5xl font-black tracking-tighter ${containerPerLot > 0 ? 'text-[#2D3E50]' : 'text-slate-300'}`}>
                          {containerPerLot}
                        </span>
                        <span className="text-lg font-black text-slate-400 uppercase tracking-widest">{t('containerInventory.addContainer.Units/Lot')}</span>
                      </div>
                      <div className="mt-8 px-6 py-2 bg-white border border-slate-200 rounded-full flex items-center gap-3 relative z-10 shadow-sm">
                         <span className="material-symbols-outlined text-emerald-500 text-lg font-black">{t('containerInventory.icons.calculate')}</span>
                         <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{t('containerInventory.addContainer.balancedDistribution')}</span>
                      </div>
                    </div>
                 </div>
              </div>
           </div>
      </div>

      {/* FOOTER ACTIONS */}
      <div className="mt-3 bg-white border-t border-slate-200 p-4 flex items-center justify-end gap-6 ">
        <button 
          onClick={onCancel}
          className="px-3 h-11 bg-white border border-slate-200 rounded-lg text-xs font-black text-slate-600 uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm"
        >
          {t('containerInventory.addContainer.cancel')}
        </button>
        
        <button 
          onClick={onSave}
          className="bg-[#FF7043] text-white flex items-center justify-center gap-1 px-2 py-2.5 rounded-lg font-black text-xs uppercase tracking-widest hover:brightness-110 active:scale-95 transition-all border border-[#FF7043] shadow-none focus:outline-none"
        >
          {t('containerInventory.addContainer.submit')}
          <span className="material-symbols-outlined text-m">{t('containerInventory.icons.add_task')}</span>
        </button>
      </div>
    </div>
  );
};

export default LogNewMediaContainerForm;
